import {
  ICPSettingsOverride,
  IToggleButton,
  IToggleButtonToReturnBoolean,
  IToggleCounterpartiesButton,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { create } from "zustand";
import { ITradeSettings } from "./tradeSettingsStoreACTIONS";

// Interface for tracking which settings fields should flash for validation errors
export interface IOrderSettingFieldToFlash {
  esid: string;
  field: "orderPriceValue" | "orderSpreadValue";
}

// Interface for tracking which leg cells should flash
export interface ILegCellToFlash {
  esid: string;
  blockId: number;
  field?: "quote" | "cash" | "setLevel"; // undefined = match all (backward compatible with existing Order usage)
  quoteSide?: "bid" | "ask" | "both"; // For quote field: which column(s) to flash. "both" = Two-Way.
}

// LQC-1040: flash entry for the OP iceberg cell (Action Summary AG Grid),
// keyed by execution request id since the row identity there is the CP's ER.
export interface IIcebergErCellToFlash {
  esid: string;
  executionRequestId: number | string;
  field: "releaseSize";
}

// LQC-1040: flash entry for the Edit Panel's iceberg inputs. `cpCode` set
// targets a per-CP override row; `cpCode` undefined targets the trade-level
// iceberg input.
export interface IIcebergCpCellToFlash {
  esid: string;
  cpCode?: string;
  field: "releaseSize";
}

export interface ITradeSettingsStore {
  counterpartiesList: IToggleCounterpartiesButton[];
  inValidCounterpartiesList: IToggleCounterpartiesButton[]; // Used to store the data for UI - Does not get sent to the API.
  tradeTypes: IToggleButton[];
  expiryTimeValue: number; // total seconds (see expiryDurationHelper, LQC-2170)
  expiryType: IToggleButton[]; // GTC | Day | Time (IOC/FOK shelved in toggleHandler)
  minimumSize: number;
  quoteType: IToggleButton[];
  releaseDirection: IToggleButtonToReturnBoolean[];
  releaseSize: number;
  releaseCompetition: IToggleButtonToReturnBoolean[];
  releaseCover: IToggleButtonToReturnBoolean[];
  releaseCoverSpread: IToggleButtonToReturnBoolean[];
  releaseCounterparty: IToggleButtonToReturnBoolean[];
  autoAffirm: IToggleButtonToReturnBoolean[];
  level: null;
  // referenceAsset: null; 15455 removed this
  // referencePrice: null; 15455 removed this
  releaseAllocation: IToggleButtonToReturnBoolean[];
  releaseSecurity: IToggleButtonToReturnBoolean[];
  // Order-specific settings
  orderSetLevelStructure: IToggleButton[];
  orderSetLevelOutright: IToggleButton[];
  orderSpreadType: IToggleButton[]; // Structures
  orderSpreadValue: number | string | undefined; // string for $fr fraction values (e.g., "99-16")
  orderPriceType: IToggleButton[]; // Outrights
  orderPriceValue: number | string | undefined; // string for $fr fraction values (e.g., "99-16")
  cpSettingsOverrides: Record<string, ICPSettingsOverride>; // Per-CP setting overrides, keyed by CP code
}

type State = {
  tradeSettingsData: ITradeSettings[];
  orderSettingFieldsToFlash: IOrderSettingFieldToFlash[];
  legCellsToFlash: ILegCellToFlash[];
  icebergErCellsToFlash: IIcebergErCellToFlash[];
  icebergCpCellsToFlash: IIcebergCpCellToFlash[];
  // Edit Panel baseline: snapshot of cpSettingsOverrides per esid at the time
  // the GET /Trade/Execution/Edit/Settings response was applied, or after a
  // successful PUT. Used to distinguish "saved" (green) from "pending" (orange)
  // states on the CP OVERRIDES rows. Presence is also the in-edit-mode signal
  // for the trade-settings actions (broadcast to active CPs, preserve overrides
  // on deselect, etc.). Absent esid → New trade flow.
  editBaselineOverrides: Record<string, Record<string, ICPSettingsOverride>>;
  // LQC-1040: every CP code returned by the GET /Trade/Execution/Edit/Settings
  // response (including Recalled / NotDealt — the API returns those too). A CP
  // code in this list has been sent the trade in the past, so the iceberg
  // floor rule applies. A CP added during the edit session via "+ Add CP"
  // that isn't here has never received the trade and has no floor — they can
  // be set to any value 1–100. After a successful PUT this list is extended
  // to include all currently-active CPs (advanceEditBaselineFromCurrent), so
  // newly-sent CPs become floor-bound for any further edits in the session.
  editKnownCpCodes: Record<string, string[]>;
  // LQC-1301 — the trade currently shown in the left-rail Trade Settings panel,
  // keyed by the widget's cryptoID so multiple open blotters don't collide.
  // The blotter gear icon (TradeWideSettingsCellRenderer) and the panel's own
  // Trade ID dropdown both write here. null entry = nothing selected for that widget.
  panelSelectedEsidByCryptoId: Record<string, string | null>;
  // LQC-1301 — per-trade-tile binding, keyed by the widget's cryptoID. In a
  // trade tile (mini table) the Trade Settings panel hides its dropdown and
  // follows the tile's own trade; ActionSummaryTable keeps this in sync as the
  // tile's selected trade changes. Keyed so multiple open tiles don't collide.
  miniTilePanelEsidByCryptoId: Record<string, string | null>;
  // LQC-1301 — snapshot of the trade-wide settings (autoAffirm) at GET time or
  // last successful PATCH. Absent entry → no baseline (New trade). Used by
  // patchSidebarTradeSettings to delta-send only changed trade-wide fields.
  // LQC-1705: releaseCoverSpread moved to the per-CP / trade-level baselines.
  editTradeWideBaselineByEsid: Record<string, { autoAffirm?: boolean }>;
  // Snapshot of the trade-level CP-overridable field values (quoteType,
  // releaseDirection, releaseCompetition, releaseCover, releaseCoverSpread,
  // releaseSize) at GET time or last successful save. Absent entry → no baseline
  // (New trade). Colouring-
  // only: drives the orange "pending" border on the trade-level row and the
  // muted-orange "following a pending default" border on CPs without an override.
  editTradeLevelBaselineByEsid: Record<string, Partial<ICPSettingsOverride>>;
};

type Action = {
  // eslint-disable-next-line no-unused-vars
  updateTradeSettingsData: (
    // eslint-disable-next-line no-unused-vars
    tradeSettingsData: State["tradeSettingsData"],
  ) => void;
  // eslint-disable-next-line no-unused-vars
  updateOrderSettingFieldsToFlash: (fields: IOrderSettingFieldToFlash[]) => void;
  clearOrderSettingFieldsToFlash: () => void;
  // eslint-disable-next-line no-unused-vars
  updateLegCellsToFlash: (cells: ILegCellToFlash[]) => void;
  clearLegCellsToFlash: () => void;
  // eslint-disable-next-line no-unused-vars
  updateIcebergErCellsToFlash: (cells: IIcebergErCellToFlash[]) => void;
  clearIcebergErCellsToFlash: () => void;
  // eslint-disable-next-line no-unused-vars
  updateIcebergCpCellsToFlash: (cells: IIcebergCpCellToFlash[]) => void;
  clearIcebergCpCellsToFlash: () => void;
  // eslint-disable-next-line no-unused-vars
  setEditBaselineForEsid: (esid: string, baseline: Record<string, ICPSettingsOverride>) => void;
  // eslint-disable-next-line no-unused-vars
  clearEditBaselineForEsid: (esid: string) => void;
  // eslint-disable-next-line no-unused-vars
  setEditKnownCpCodesForEsid: (esid: string, codes: string[]) => void;
  // eslint-disable-next-line no-unused-vars
  clearEditKnownCpCodesForEsid: (esid: string) => void;
  // eslint-disable-next-line no-unused-vars
  setPanelSelectedEsid: (cryptoId: string, esid: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  setMiniTilePanelEsid: (cryptoId: string, esid: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  setEditTradeWideBaselineForEsid: (esid: string, baseline: { autoAffirm?: boolean; releaseCoverSpread?: string }) => void;
  // eslint-disable-next-line no-unused-vars
  setEditTradeLevelBaselineForEsid: (esid: string, baseline: Partial<ICPSettingsOverride>) => void;
};

const tradeSettingsStore = create<State & Action>((set) => ({
  tradeSettingsData: [],
  orderSettingFieldsToFlash: [],
  legCellsToFlash: [],
  icebergErCellsToFlash: [],
  icebergCpCellsToFlash: [],
  editBaselineOverrides: {},
  editKnownCpCodes: {},
  panelSelectedEsidByCryptoId: {},
  miniTilePanelEsidByCryptoId: {},
  editTradeWideBaselineByEsid: {},
  editTradeLevelBaselineByEsid: {},
  updateTradeSettingsData: (
    incomingData: {
      esid: string;
      settings: ITradeSettingsStore;
    }[],
  ) => {
    set(() => ({
      tradeSettingsData: incomingData,
    }));
  },
  updateOrderSettingFieldsToFlash: (fields: IOrderSettingFieldToFlash[]) => {
    set(() => ({
      orderSettingFieldsToFlash: fields,
    }));
  },
  clearOrderSettingFieldsToFlash: () => {
    set(() => ({
      orderSettingFieldsToFlash: [],
    }));
  },
  updateLegCellsToFlash: (cells: ILegCellToFlash[]) => {
    set(() => ({
      legCellsToFlash: cells,
    }));
  },
  clearLegCellsToFlash: () => {
    set(() => ({
      legCellsToFlash: [],
    }));
  },
  updateIcebergErCellsToFlash: (cells: IIcebergErCellToFlash[]) => {
    set(() => ({
      icebergErCellsToFlash: cells,
    }));
  },
  clearIcebergErCellsToFlash: () => {
    set(() => ({
      icebergErCellsToFlash: [],
    }));
  },
  updateIcebergCpCellsToFlash: (cells: IIcebergCpCellToFlash[]) => {
    set(() => ({
      icebergCpCellsToFlash: cells,
    }));
  },
  clearIcebergCpCellsToFlash: () => {
    set(() => ({
      icebergCpCellsToFlash: [],
    }));
  },
  setEditBaselineForEsid: (esid, baseline) => {
    set((state) => ({
      editBaselineOverrides: { ...state.editBaselineOverrides, [esid]: baseline },
    }));
  },
  clearEditBaselineForEsid: (esid) => {
    set((state) => {
      const next = { ...state.editBaselineOverrides };
      delete next[esid];
      return { editBaselineOverrides: next };
    });
  },
  setEditKnownCpCodesForEsid: (esid, codes) => {
    set((state) => ({
      editKnownCpCodes: { ...state.editKnownCpCodes, [esid]: codes },
    }));
  },
  clearEditKnownCpCodesForEsid: (esid) => {
    set((state) => {
      const next = { ...state.editKnownCpCodes };
      delete next[esid];
      return { editKnownCpCodes: next };
    });
  },
  setPanelSelectedEsid: (cryptoId, esid) => {
    set((state) => {
      if (state.panelSelectedEsidByCryptoId[cryptoId] === esid) return state;
      return {
        panelSelectedEsidByCryptoId: {
          ...state.panelSelectedEsidByCryptoId,
          [cryptoId]: esid,
        },
      };
    });
  },
  setMiniTilePanelEsid: (cryptoId, esid) => {
    set((state) => {
      if (state.miniTilePanelEsidByCryptoId[cryptoId] === esid) return state;
      return {
        miniTilePanelEsidByCryptoId: {
          ...state.miniTilePanelEsidByCryptoId,
          [cryptoId]: esid,
        },
      };
    });
  },
  setEditTradeWideBaselineForEsid: (esid, baseline) => {
    set((state) => ({
      editTradeWideBaselineByEsid: { ...state.editTradeWideBaselineByEsid, [esid]: baseline },
    }));
  },
  setEditTradeLevelBaselineForEsid: (esid, baseline) => {
    set((state) => ({
      editTradeLevelBaselineByEsid: { ...state.editTradeLevelBaselineByEsid, [esid]: baseline },
    }));
  },
}));

export default tradeSettingsStore;
